import { createElement, ReactNode } from 'react';
import { optimize, XastChild, XastRoot } from 'svgo/browser';
import { camelCase } from "lodash";
import cx from 'classnames';

const sheet = new CSSStyleSheet();

function svgoAstToReactNode(
  ast: XastRoot | XastChild,
  { key, props }: { key: string, props?: React.SVGProps<SVGSVGElement> },
): ReactNode {
  if (ast.type === 'root') {
    return ast.children
      .map((child, i) => svgoAstToReactNode(child, { key: key + ast.type + i, props }));
  } else if (ast.type === 'element') {
    const { style: rawStyle, class: attributesClassName, ...rawAttributes } = ast.attributes;
    const { style: propsStyle, className: propsClassName, ...otherProps } = props ?? { style: {}, className: '' };

    // Parse the style attribute to convert into an object with camelCase keys, acceptable for React
    sheet.replaceSync(`*{${rawStyle}}`);
    const styleDeclaration: CSSStyleDeclaration = (sheet?.cssRules[0] as CSSStyleRule).style;
    const style = Object.fromEntries(
      Array(styleDeclaration.length).fill(undefined)
        .map((_, i) => {
          return [
            camelCase(styleDeclaration[i]),
            styleDeclaration[styleDeclaration[i] as any],
          ];
        }),
    );

    // Convert namespaced attributes to camelcase, for React
    const attributes = Object.fromEntries(Object.entries(rawAttributes).map(([key, value]) => [
      camelCase(key.replace(':', '-')),
      value
    ]));

    return createElement(
      ast.name,
      {
        ...attributes,
        ...otherProps,
        key: key + ast.type,
        style: { ...style, propsStyle },
        className: cx(attributesClassName, propsClassName),
      },
      ast.children.map((child, i) => svgoAstToReactNode(child, { key: key + ast.type + i, props: undefined })),
    );
  } else if (ast.type === 'text') {
    return ast.value;
  } else {
    throw new Error('Unsupported SVGO AST node');
  }
}

export function optimizeAndParseSVG(svgData: string): XastRoot {
  let ast: XastRoot = { type: 'root', children: [] };

  optimize(svgData, {
    floatPrecision: 3,
    plugins: [
      'removeScripts',
      'removeComments',
      'removeDoctype',
      'removeXMLProcInst',
      'removeMetadata',
      'removeTitle',
      'removeDesc',
      'removeEditorsNSData',
      'convertPathData',
      'convertTransform',
      'cleanupNumericValues',
      // Keep the optimized AST, to build React elements from it
      { name: 'keepAst', fn: (root) => { ast = root; } },
    ],
  });

  return ast;
}

export function optimizeAndParseSVGToComponent(svgData: string, props: Record<string, any> = {}): ReactNode {
  const ast = optimizeAndParseSVG(svgData);

  return svgoAstToReactNode(ast, { key: '', props });
}
